import { useParams, Outlet, NavLink, Link } from "react-router-dom";
import { useSingleBrand } from "@/hooks/queries/useSingleBrand";
import HeaderTemplate from "@/components/partials/HeaderTemplate";
import type { Brand } from "@/types/brand";
import { Helmet } from "react-helmet-async";
import useSubdomain from "@/hooks/useSubdomain";
import { useGetBrandByDomain } from "@/hooks/queries/useBrandQueries";
import BrandNotFoundPage from "@/pages/manager/brand/BrandNotFoundPage";
import GenericLoader from "@/components/ui/GenericLoader";

export interface BrandContextType {
  brand: Brand;
}

export default function BrandLayout() {
  const { brandSlug } = useParams<{ brandSlug: string }>();
  const subdomain = useSubdomain();
  const {
    data: domainBrand,
    isLoading: isDLoading,
    isError: isDError,
  } = useGetBrandByDomain(subdomain || "");
  const { data: slugBrand, isLoading, isError } = useSingleBrand(brandSlug);

  const brand = domainBrand ?? slugBrand!;

  if (isDLoading || isLoading) {
    return (
      <GenericLoader type={"page"} message={"Getting things ready"} appendMessages={["Almost done", "Okay, here we go"]}/>
    );
  }

  if (isError || isDError || !brand) {
    return <BrandNotFoundPage />;
  }

  return (
    <div className="flex min-h-screen flex-col bg-gray-50 text-gray-900">
      <Helmet>
        <title>{`${brand?.name} | Muciifi`}</title>

        <meta
          name="description"
          content={
            brand?.description ||
            `Discover ${brand?.name} on Muciifi. Find available accommodations and contact the property directly.`
          }
        />

        {/* Open Graph — WhatsApp, Facebook, LinkedIn, etc. */}
        <meta property="og:title" content={brand?.name || "Muciifi"} />
        <meta
          property="og:description"
          content={brand?.description || `Discover ${brand?.name} on Muciifi.`}
        />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={window.location.href} />

        {/* Optional but highly recommended */}
        {brand?.logoUrl && <meta property="og:image" content={brand.logoUrl} />}

        {/* Twitter/X */}
        <meta name="twitter:card" content="summary" />
        <meta name="twitter:title" content={brand?.name || "Muciifi"} />
        <meta
          name="twitter:description"
          content={brand?.description || `Discover ${brand?.name} on Muciifi.`}
        />
        {brand?.logoUrl && (
          <meta name="twitter:image" content={brand.logoUrl} />
        )}
      </Helmet>
      {/* Fixed / Sticky Header */}
      <header className="sticky top-0 z-50 h-16 w-full border-b border-gray-200 bg-white/95 px-6 backdrop-blur-md">
        <HeaderTemplate centerJustify="center">
          {/* Index 0: Left Slot (Logo & Brand Name) */}
          <Link
            to={`/`}
            className="flex items-center gap-3 font-semibold text-gray-900 hover:opacity-90"
          >
            {brand.logoUrl ? (
              <img
                src={brand.logoUrl}
                alt={brand.name}
                className="h-9 w-9 rounded-lg object-cover"
              />
            ) : (
              <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-blue-600 font-bold text-white">
                {brand.name.charAt(0)}
              </div>
            )}
            <span className="text-base font-bold">{brand.name}</span>
          </Link>

          {/* Index 1: Center Slot (Navigation Links) */}
          <nav className="flex items-center gap-6">
            <NavLink
              to={`/`}
              end
              className={({ isActive }) =>
                `text-sm font-medium transition-colors ${
                  isActive
                    ? "text-blue-600"
                    : "text-gray-600 hover:text-gray-900"
                }`
              }
            >
              Home
            </NavLink>

            <NavLink
              to={`/listings`}
              className={({ isActive }) =>
                `text-sm font-medium transition-colors ${
                  isActive
                    ? "text-blue-600"
                    : "text-gray-600 hover:text-gray-900"
                }`
              }
            >
              Listings
            </NavLink>
            <NavLink to={"#"}>Our Properties</NavLink>
            <NavLink to={"#"}>Acconmmodation</NavLink>
            <NavLink
              to={`/contact`}
              className={({ isActive }) =>
                `text-sm font-medium transition-colors ${
                  isActive
                    ? "text-blue-600"
                    : "text-gray-600 hover:text-gray-900"
                }`
              }
            >
              Contact
            </NavLink>
          </nav>

          {/* Index 2: Right Slot (Action Callouts) */}
          <div className="flex items-center gap-3">
            <Link
              to={`/contact`}
              className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white transition hover:bg-blue-700"
            >
              Inquire Now
            </Link>
          </div>
        </HeaderTemplate>
      </header>

      {/* Main Outlet View */}
      <main className="flex-1">
        <Outlet context={{ brand } satisfies BrandContextType} />
      </main>

      {/* Footer */}
      <footer className="border-t border-gray-200 bg-white py-8">
        <div className="mx-auto max-w-6xl px-6 text-center text-sm text-gray-500">
          <p>
            © {new Date().getFullYear()} {brand.name}. Powered by Muciifi.
          </p>
        </div>
      </footer>
    </div>
  );
}
